// src/pages/Transactions/styles.js
import { COLORS, LAYOUT, TYPOGRAPHY } from '../../constants';

export const transactionStyles = {
  // Page Container
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: LAYOUT.background,
    padding: '40px 20px',
  },

  // Main Container
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
  },

  // Header styles
  header: {
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, #a00e1f 100%)`,
    padding: '48px 40px',
    borderRadius: '16px',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: '32px',
    boxShadow: '0 4px 16px rgba(206, 17, 38, 0.2)',
  },

  iconWrapper: {
    width: '64px',
    height: '64px',
    margin: '0 auto 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)',
  },

  title: {
    fontSize: '32px',
    fontWeight: '700',
    margin: '0 0 12px 0',
    letterSpacing: '-0.5px',
  },

  subtitle: {
    fontSize: '16px',
    fontWeight: '400',
    margin: 0,
    opacity: 0.9,
    letterSpacing: '0.2px',
  },

  // Filters Container
  filtersContainer: {
    marginBottom: '32px',
  },

  filters: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },

  filterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    border: '2px solid #e0e0e0',
    backgroundColor: '#ffffff',
    color: COLORS.secondary,
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    outline: 'none',
    fontFamily: TYPOGRAPHY.fontFamily,
  },

  filterButtonActive: {
    backgroundColor: COLORS.primary,
    color: '#ffffff',
    borderColor: COLORS.primary,
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px rgba(206, 17, 38, 0.3)`,
  },

  filterIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Transactions List
  transactionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  // Transaction Card
  transactionCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    transition: 'all 0.3s ease',
    ':hover': {
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-2px)',
    },
  },

  transactionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f0f0f0',
  },

  transactionType: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  typeIconWrapper: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    backgroundColor: '#fef5f6',
    color: COLORS.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  typeText: {
    fontWeight: '700',
    fontSize: '18px',
    color: COLORS.secondary,
    letterSpacing: '-0.3px',
  },

  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
  },

  statusText: {
    textTransform: 'capitalize',
  },

  transactionDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '24px',
    marginBottom: '20px',
  },

  itemInfo: {
    flex: '1',
  },

  itemTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: COLORS.secondary,
    marginBottom: '10px',
    lineHeight: '1.4',
    margin: '0 0 10px 0',
  },

  otherParty: {
    fontSize: '15px',
    color: '#666',
  },

  partyName: {
    fontWeight: '600',
    color: COLORS.primary,
  },

  amountInfo: {
    textAlign: 'right',
    minWidth: '140px',
  },

  totalAmount: {
    fontSize: '28px',
    fontWeight: '700',
    color: COLORS.primary,
    marginBottom: '8px',
    letterSpacing: '-0.5px',
  },

  amountBreakdown: {
    fontSize: '13px',
    color: '#666',
    lineHeight: '1.6',
  },

  breakdownItem: {
    marginBottom: '4px',
  },

  commissionFee: {
    color: COLORS.primary,
    fontWeight: '500',
  },

  transactionFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #f0f0f0',
    fontSize: '14px',
    color: '#666',
  },

  transactionDate: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  transactionId: {
    fontFamily: 'monospace',
    fontSize: '13px',
    color: '#999',
  },

  // Loading State
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    textAlign: 'center',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e5e5e5',
  },

  spinnerAnimation: {
    color: COLORS.primary,
    animation: 'spin 1.5s linear infinite',
    marginBottom: '24px',
  },

  loadingTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: COLORS.secondary,
    margin: '0 0 8px 0',
  },

  loadingText: {
    fontSize: '15px',
    color: '#666',
    margin: 0,
  },

  // Empty State
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '80px 20px',
    textAlign: 'center',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e5e5e5',
  },

  emptyIcon: {
    color: COLORS.primary,
    opacity: 0.5,
    marginBottom: '24px',
  },

  emptyTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: COLORS.secondary,
    margin: '0 0 12px 0',
  },

  emptyMessage: {
    fontSize: '16px',
    color: '#666',
    maxWidth: '500px',
    lineHeight: '1.6',
    margin: 0,
  },

  // No User State
  noUserContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '80px 20px',
    textAlign: 'center',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e5e5e5',
    maxWidth: '600px',
    margin: '0 auto',
  },

  noUserIcon: {
    color: COLORS.primary,
    opacity: 0.5,
    marginBottom: '24px',
  },

  noUserTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: COLORS.secondary,
    margin: '0 0 12px 0',
  },

  noUserMessage: {
    fontSize: '16px',
    color: '#666',
    margin: 0,
  },
};

// Status colors function
export const getStatusColor = (status) => {
  const colors = {
    'completed': '#10B981',
    'pending': '#F59E0B', 
    'failed': '#EF4444',
    'processing': '#3B82F6',
    'cancelled': '#6B7280'
  };
  return colors[status?.toLowerCase()] || colors.completed;
};